import { useState } from "react";
import useI18n from "@/hooks/useI18n";
import { Flex, Box } from "@styled-system/jsx";
import NotIncludedIcon from "@/assets/common/not-included.svg?react";
import { css, cx } from "@styled-system/css";
import ArrowUpIcon from "@/assets/common/arrow-down.svg?react";
import IncludedIcon from "@/assets/common/included.svg?react";
import { StarIcon } from "@/appContent/pricing";
import { button } from "@styled-system/recipes";
import { comparisionButtonText } from "@/appContent/pricing";
import { IconAsButton } from "@/components/react/IconAsButton";
import { comparisonEnum } from "./comparisonEnum";
import DescriptionCollapse from "../DescriptionCollapse";

export default function Comparison() {
  const { t } = useI18n();
  const [expandedDescriptions, setExpandedDescriptions] = useState<{
    [key: string]: boolean;
  }>({});

  const handleToggleExpand = (txt: string) => {
    setExpandedDescriptions((prev) => ({ ...prev, [txt]: !prev[txt] }));
  };

  return (
    <>
      <h3 className={css({ mt: "7rem", mb: "3.5rem", textAlign: "center" })}>{t("pricing.whyUpgrade")}</h3>

      <Flex
        className={css({
          alignItems: "flex-end",
          justifyContent: "flex-end",
          borderStyle: "solid",
          borderWidth: "1px 1px 0px 1px",
          borderColor: "lowBorder",
          borderTopLeftRadius: "1rem",
          borderTopRightRadius: "1rem",
          pt: ["0.5rem", "1rem"],
          pb: ["0.5rem", "1rem"]
        })}
      >
        <Flex
          className={css({
            flexBasis: ["25%", "20%"],
            fontWeight: 500,
            flexDirection: ["column", "row"],
            alignItems: "center",
            justifyContent: "center"
          })}
        >
          <h5 className={css({ my: 0 })}>{t("free")}</h5>
        </Flex>
        <Flex
          className={css({
            flexBasis: ["25%", "20%"],
            fontWeight: 500,
            flexDirection: ["column", "row"],
            alignItems: "center",
            justifyContent: "center"
          })}
        >
          <StarIcon className={css({ mr: "0.25rem" })} />
          <h5 className={css({ my: 0 })}>{t("premium")}</h5>
        </Flex>
      </Flex>
      <Flex
        className={css({
          justifyContent: "center",
          flexDirection: "column",
          rowGap: "12px",
          border: "1px solid",
          borderColor: "lowBorder",
          borderBottomLeftRadius: "1rem",
          borderBottomRightRadius: "1rem",
          py: "1.25rem",
          position: "relative"
        })}
      >
        <Box
          bg="lowBorder"
          className={css({
            position: "absolute",
            height: "100%",
            right: ["25%", "20%"],
            width: "1px"
          })}
        />
        <Box
          bg="lowBorder"
          className={css({
            position: "absolute",
            height: "100%",
            right: ["49%", "40%"],
            width: "1px"
          })}
        />
        {comparisonEnum.map((feature) => {
          const isExpanded = expandedDescriptions[feature.string];
          return (
            <Flex
              onClick={() => handleToggleExpand(feature.string)}
              key={feature.string}
              className={css({
                display: "flex",
                pos: "relative",
                alignItems: "flex-start"
              })}
            >
              <Flex
                className={css({
                  flexBasis: ["100%", "60%"],
                  flexDirection: "column",
                  mb: "0",
                  py: "0",
                  px: ["1rem", "2rem"],
                  bg: "transparent",
                  border: "none"
                })}
              >
                <Flex maxW={["50%", "100%"]} alignItems="center">
                  <h5 className={css({ mb: 0, mt: "0.25rem", textAlign: "start" })}>{t(feature.string)}</h5>
                  {feature.description && (
                    <IconAsButton
                      Icon={ArrowUpIcon}
                      size={24}
                      iconFill="mediumEmphasis"
                      className={css({
                        transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                        ml: "0.5rem"
                      })}
                    />
                  )}
                </Flex>
                <DescriptionCollapse isExpanded={isExpanded} feature={feature} />
              </Flex>
              <Flex
                className={css({
                  justifyContent: "center",
                  alignItems: "center",
                  flexBasis: "20%",
                  position: ["absolute", null, "static"],
                  transform: ["translateX(50%)", null, "none"],
                  right: ["37.5%", "30%"]
                })}
              >
                {feature.freeStringKey ? (
                  <p
                    className={css({
                      color: "mediumEmphasis",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "14px",
                      lineHeight: "16px",
                      letterSpacing: 0.25
                    })}
                  >
                    {t(feature.freeStringKey)}
                  </p>
                ) : (
                  <Flex
                    className={css({
                      width: "2.5rem",
                      height: "2.5rem",
                      borderRadius: "100%",
                      justifyContent: "center",
                      alignItems: "center"
                    })}
                  >
                    {feature.includedInFreeVersion ? (
                      <IncludedIcon className={css({ fill: "success" })} />
                    ) : (
                      <NotIncludedIcon className={css({ fill: "warning", opacity: 0.6 })} />
                    )}
                  </Flex>
                )}
              </Flex>
              <Flex
                className={css({
                  justifyContent: "center",
                  alignItems: "center",
                  flexBasis: "20%",
                  position: ["absolute", null, "static"],
                  transform: ["translateX(50%)", null, "none"],
                  right: ["12.5%", "10%"]
                })}
              >
                {feature.premiumStringKey ? (
                  <p
                    color="mediumEmphasis"
                    className={css({
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "14px",
                      lineHeight: "16px",
                      letterSpacing: 0.25
                    })}
                  >
                    {t(feature.premiumStringKey)}
                  </p>
                ) : (
                  <Flex
                    className={css({
                      width: "2.5rem",
                      height: "2.5rem",
                      borderRadius: "100%",
                      justifyContent: "center",
                      alignItems: "center"
                    })}
                  >
                    <IncludedIcon className={css({ fill: "success" })} />
                  </Flex>
                )}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
      <button
        onClick={() => {
          window.scrollTo({
            behavior: "smooth",
            top: 0
          });
        }}
        className={cx(
          button({ variant: "filled" }),
          css({
            display: "block",
            mx: "auto",
            mt: ["4.5rem", "7rem"]
          })
        )}
      >
        {t(comparisionButtonText)}
      </button>
    </>
  );
}
